/**
 * @author Sasanka Weerakoon
 * @version 0.0.1
 * @since 24/4/2025
 */

import React, { useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Button } from "@mui/material";
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';

const OrganizationReportTable = ({ organizationReportData, loading }) => {

    const data = organizationReportData;

     const columns = useMemo(
            () => [
                {
                    accessorKey: "orgName",
                    header: "Org Name",
                    size: 50,
                },
                {
                    accessorKey: "orgCode",
                    header: "Org Code",
                    size: 50,
                },
                {
                    accessorKey: "primAddress",
                    header: "Prim Address",
                    size: 50,
                },
                {
                    accessorKey: "email",
                    header: "Email",
                    size: 50,
                },
                {
                    accessorKey: "primContact",
                    header: "Prim Contact",
                    size: 50,
    
                },
                {
                    accessorKey: "grpTmnlSummary.totGroups",
                    header: "Tot Groups",
                    size: 50,
                },
                {
                    accessorKey: "grpTmnlSummary.totTerminals",
                    header: "Tot Terminals",
                    size: 50,
                },
                {
                    accessorKey: "grpTmnlSummary.activeTerminals",
                    header: "Active Terminals",
                    size: 50,
                }
            ], []);
    
        const handleExportPDF = () => {
            const doc = new jsPDF({
                orientation: "landscape",
                unit: "pt",
                format: "A4",
            });
    
            doc.setFontSize(16);
            doc.text("Organization Report", 40, 40);
    
            const tableColumn = columns.map(col => col.header);
            const tableRows = data.map(row =>
                columns.map(col => {
                    const accessorKeys = col.accessorKey.split(".");
                    let value = row;
                    for (let key of accessorKeys) {
                        value = value?.[key];
                    }
                    return value !== undefined && value !== null ? String(value) : "";
                })
            );
    
            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 60,
                margin: { left: 40, right: 40 },
                styles: {
                    fontSize: 8,
                    cellPadding: 4,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: [22, 160, 133],
                    textColor: 255,
                    fontStyle: 'bold',
                },
    
            });
    
            doc.save("organization_report.pdf");
        }
    
    
        const handleExportExcel = async () => {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Organization Report");
    
            // Define columns including nested fields
            worksheet.columns = [
                { header: "ORG NAME", key: "orgName" },
                { header: "ORG CODE", key: "orgCode" },
                { header: "PRIM ADDRESS", key: "primAddress" },
                { header: "SEC ADDRESS", key: "secAddress" },
                { header: "EMAIL", key: "email" },
                { header: "PRIM CONTACT", key: "primContact" },
                { header: "SEC CONTACT", key: "secContact" },
                { header: "TOT GROUPS", key: "totGroups" },
                { header: "TOT TERMINALS", key: "totTerminals" },
                { header: "ACTIVE TERMINALS", key: "activeTerminals" },
                { header: "LTS APP VER", key: "latestAppVersion" },
                { header: "LTS APP CREATED TIME", key: "latestAppCreatedTime" }
            ];
    
            // Add data rows by flattening the nested structure
            data.forEach(item => {
                worksheet.addRow({
                    orgName: item.orgName,
                    orgCode: item.orgCode,
                    primAddress: item.primAddress,
                    secAddress: item.secAddress,
                    email: item.email,
                    primContact: item.primContact,
                    secContact: item.secContact,
                    totGroups: item.grpTmnlSummary?.totGroups || 0,
                    totTerminals: item.grpTmnlSummary?.totTerminals || 0,
                    activeTerminals: item.grpTmnlSummary?.activeTerminals || 0,
                    latestAppVersion: item.grpTmnlSummary?.latestAppVersion || '',
                    latestAppCreatedTime: item.grpTmnlSummary?.latestAppCreatedTime || ''
                });
            });
    
            worksheet.getRow(1).font = { bold: true };
    
    
            worksheet.columns.forEach((column) => {
                let maxLength = column.header.length;
                column.eachCell({ includeEmpty: true }, (cell) => {
                    if (cell.value) {
                        const cellValueLength = cell.value.toString().length;
                        if (cellValueLength > maxLength) {
                            maxLength = cellValueLength;
                        }
                    }
                });
                column.width = maxLength + 2;
            });
    
            // Export the Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/octet-stream' });
            saveAs(blob, 'organization_report.xlsx');
        };
    
        const isNumeric = (value) => {
            return !isNaN(value) && value !== null && value !== "" && typeof value !== "boolean";
        }

        return (
            <MaterialReactTable
                            columns={columns}
                            data={data ?? []}
                            state={{
                                showProgressBars: loading,
                                showSkeletons: loading
                            }}
                            muiTableContainerProps={{
                                sx: {
                                    overflowX: "auto",
                                    paddingLeft: "1rem",
                                    paddingRight: "1rem",
                                },
                            }}
                            muiTableBodyCellProps={({ cell }) => ({
                                sx: data.length > 0 
                                    ? {
                                          wordWrap: "break-word",
                                          whiteSpace: "normal",
                                          maxWidth: "160px",
                                          textAlign: isNumeric(cell.getValue()) ? "right" : "left",
                                      }
                                    : {}, 
                            })}
                            muiLinearProgressProps={{
                                sx: { position: "absolute", bottom: 0, left: 0, right: 0 },
                            }}
                            enableColumnActions={false}
                            enableSorting={false}
                            enablePagination={true}
                            initialState={{
                                pagination: { pageSize: 10, pageIndex: 0 }
                            }}
                            positionActionsColumn="last"
                            muiTableBodyRowProps={({ row }) => ({
                                onClick: () => console.log("Row clicked:", row.original),
                                sx: { cursor: "pointer" }
                            })}
                            renderTopToolbarCustomActions={({ table }) => (
                                <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<PrintIcon />}
                                        onClick={handleExportPDF}
                                        sx={{ marginLeft: 2 }}
                                    >
                                        PDF
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<TableChartIcon />}
                                        onClick={handleExportExcel}
                                        sx={{ marginLeft: 2 }}
                                    >
                                        XLSX
                                    </Button>
                                </div>
                            )}
                            renderEmptyRowsFallback={() => (
                                <div style={{ textAlign: "center", padding: "1rem" }}>
                                    No data available
                                </div>
                            )}
                        />
        )
}

export default OrganizationReportTable;